/**
 * Getting bytes out of the tab.
 *
 * Nothing here talks to a network. A download is an object URL and a synthetic
 * click; the clipboard is the async API with a `execCommand` fallback for
 * browsers (and insecure contexts) that do not expose it.
 */

/** How long to keep an object URL alive after the click. */
const REVOKE_DELAY_MS = 1000;

export function downloadBlob(blob: Blob, filename: string): void {
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoking immediately cancels the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}

export function downloadText(text: string, filename: string, mime: string): void {
  downloadBlob(new Blob([text], { type: `${mime};charset=utf-8` }), filename);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied or insecure context — fall through to the fallback.
    }
  }

  if (typeof document === 'undefined') return false;
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    return copied;
  } catch {
    return false;
  }
}

/** Reads a dropped or picked file as text, with a size guard. */
export async function readFileAsText(file: File, maxBytes = 5_000_000): Promise<string> {
  if (file.size > maxBytes) {
    throw new Error(`That file is too large (${file.size} bytes; the limit is ${maxBytes}).`);
  }
  return file.text();
}
